import React, { useEffect, useState } from "react";
import { SelectField, Tooltip, useForm } from "@culturehq/components";
import * as CHQ from "chq";

import { useGet } from "app/hooks";
import { t, Trans } from "app/utils";

export const noSlackBroadcastValue = "no-broadcast";

type SlackChannelTooltipProps = SlackChannelFieldProps & {
  children: React.ReactNode;
};

const SlackChannelTooltip: React.FC<SlackChannelTooltipProps> = ({
  children, publicEvent, inviteOnly
}) => {
  if (!publicEvent) {
    return (
      <Tooltip tip={t("Cannot broadcast private events to Slack.")}>
        {children}
      </Tooltip>
    );
  }

  if (inviteOnly) {
    return (
      <Tooltip tip={t("Cannot broadcast invite-only events to Slack.")}>
        {children}
      </Tooltip>
    );
  }

  return <>{children}</>;
};

type SlackChannel = {
  name: string;
  id: string;
};

type ListSlackChannels = {
  slackIntegration: CHQ.SlackIntegration;
  slackChannels: SlackChannel[];
};

type SlackChannelFieldProps = {
  publicEvent?: boolean;
  inviteOnly?: boolean;
  showLabel?: boolean;
  className?: string;
  disabled?: boolean;
  imageIconPath?: string;
  onSelectedChannel?: (_channel: string) => void;
  noBroadcastAllowed?: boolean;
  channel?: string;
  page?: string;
};

const SlackChannelField: React.FC<SlackChannelFieldProps> = ({
  publicEvent = true,
  inviteOnly = false,
  showLabel = true,
  className = "",
  disabled = false,
  imageIconPath,
  onSelectedChannel,
  noBroadcastAllowed = true,
  channel,
  page
}) => {
  const { onFormChange } = useForm();
  const [selectedChannel, setSelectedChannel] = useState(channel || "");
  const get = useGet<ListSlackChannels>("/slack_integration/slack_channels");

  // We only want to do this once, so we're just going to memoize based off the
  // get response so that it doesn't change once we get it.
  useEffect(
    () => {
      if (get.getting || get.error) {
        return;
      }

      const { slackChannels, slackIntegration } = get.got;
      const { channel } = slackIntegration;

      let name = channel[0] === "#" ? channel.slice(1) : channel;
      if (page) {
        const defaultChannel = slackIntegration.slackDefaultChannels.find(
          defaultChannel => defaultChannel.data === page
        );
        if (defaultChannel) {
          name = defaultChannel.channel[0] === "#" ? defaultChannel.channel.slice(1) : defaultChannel.channel;
        }
      }

      const matched = slackChannels.find(slackChannel => slackChannel.name === name);

      if (matched) {
        if (selectedChannel === "") {
          setSelectedChannel(matched.id);
          if (onSelectedChannel) {
            onSelectedChannel(matched.id);
          }
        }

        onFormChange("slackChannel", matched.id);
      } else {
        setSelectedChannel(noSlackBroadcastValue);
        onFormChange("slackChannel", noSlackBroadcastValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [get.getting, get.error, get.got]
  );

  // If we're loading or we received back an error, then just ignore this field
  // entirely and display nothing.
  if (get.getting || get.error) {
    return null;
  }

  const onSelectedValue = (value: any) => {
    setSelectedChannel(value);
    if (onSelectedChannel) {
      onSelectedChannel(value);
    }
  };

  const noBroadcast = { label: "Do not broadcast", value: noSlackBroadcastValue };
  const array = noBroadcastAllowed ? [noBroadcast] : [];
  const fieldOptions = array.concat(get.got.slackChannels.map(slackChannel => ({
    label: `#${slackChannel.name}`, value: slackChannel.id
  })));

  return (
    <SelectField
      ariaLabel={t("Select slack channel")}
      className={`${className} m-b-30 slack-channel-field`}
      name="slackChannel"
      imageIconPath={imageIconPath}
      options={fieldOptions}
      disabled={!publicEvent || inviteOnly || disabled}
      onChange={onSelectedValue}
      value={selectedChannel}
      aria-label="Slack channel"
    >
      {showLabel && (
        <SlackChannelTooltip publicEvent={publicEvent} inviteOnly={inviteOnly}>
          <Trans>Broadcast this event to Slack</Trans>
        </SlackChannelTooltip>
      )}
    </SelectField>
  );
};

export default SlackChannelField;
